import React, { useState } from 'react';
import { Beaker, FileCheck, AlertCircle, Target, Search, ChevronRight, Printer, Share2, ExternalLink } from 'lucide-react';
import { useLabSample } from '../../contexts/LabSampleContext';
import { useFracas } from '../../contexts/FracasContext';
import { useAuth } from '../../contexts/AuthContext';

export const ReportGeneratorHub: React.FC = () => {
    const { samples } = useLabSample();
    const { incidents } = useFracas();
    const { user } = useAuth();

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [template, setTemplate] = useState<string | null>(null);
    const [selectedDataId, setSelectedDataId] = useState<string | null>(null);

    const templates = [
        {
            id: 'lab-analysis',
            title: 'Lab Analysis Report',
            description: 'Standard lab test results with findings, photos, and conclusion.',
            icon: <Beaker className="w-8 h-8 text-blue-500" />,
            compliance: 'ISO 17025',
            dataType: 'lab'
        },
        {
            id: '8d-customer',
            title: '8D Problem Solving Report',
            description: 'Full 8-step discipline report for customer submission.',
            icon: <FileCheck className="w-8 h-8 text-green-500" />,
            compliance: 'AIAG / VDA',
            dataType: '8d'
        },
        {
            id: 'failure-analysis',
            title: 'Failure Analysis Summary',
            description: 'Root cause investigation summary linked to FRACAS.',
            icon: <AlertCircle className="w-8 h-8 text-red-500" />,
            compliance: 'Internal',
            dataType: 'fracas'
        },
        {
            id: 'reliability-qual',
            title: 'Reliability Qualification (DVP&R)',
            description: 'Validation testing summary against reliability targets.',
            icon: <Target className="w-8 h-8 text-purple-500" />,
            compliance: 'IATF 16949',
            dataType: 'lab'
        }
    ];

    const getAvailableData = () => {
        const selectedTemplate = templates.find(t => t.id === template);
        if (!selectedTemplate) return [];

        if (selectedTemplate.dataType === 'lab') {
            return samples.filter(s => s.status === 'complete').map(s => ({
                id: s.id,
                title: `${s.sampleNumber} - ${s.product}`,
                subtitle: `${s.requestedTests.length} tests completed • ${s.testResults?.some(r => r.result === 'fail') ? 'FAIL' : 'PASS'}`,
                date: s.updatedAt
            }));
        }
        if (selectedTemplate.dataType === 'fracas') {
            return incidents.map(i => ({
                id: i.id,
                title: `${i.title}`,
                subtitle: `Status: ${i.status} • Severity: ${i.severity}`,
                date: i.reportedAt
            }));
        }
        return [];
    };

    const renderReportPreview = () => {
        const selectedSample = samples.find(s => s.id === selectedDataId);
        const selectedTemplate = templates.find(t => t.id === template);

        if (!selectedSample && selectedTemplate?.dataType === 'lab') return <div>Data not found</div>;

        return (
            <div className="bg-white p-8 shadow-sm border border-gray-200 min-h-[800px] w-full max-w-4xl mx-auto" id="report-preview">
                <div className="border-b-2 border-gray-800 pb-4 mb-8 flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedTemplate?.title?.toUpperCase()}</h1>
                        <p className="text-gray-600">Doc Ref: MAG-{new Date().getFullYear()}-{Math.floor(Math.random() * 1000)}</p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600 tracking-tighter">MAGNUS<span className="text-gray-900">RESOLVE</span></div>
                        <p className="text-sm text-gray-500">Quality Assurance Dept.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div className="space-y-2">
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Date:</span>
                            <span>{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Report By:</span>
                            <span>{user?.email}</span>
                        </div>
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Status:</span>
                            <span className="bg-yellow-100 text-yellow-800 px-2 rounded-full text-xs font-bold uppercase">Draft</span>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Sample ID:</span>
                            <span>{selectedSample?.sampleNumber}</span>
                        </div>
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Product:</span>
                            <span>{selectedSample?.product}</span>
                        </div>
                        <div className="flex border-b border-gray-100 py-1">
                            <span className="font-bold w-32">Serial:</span>
                            <span>{selectedSample?.serialNumber}</span>
                        </div>
                    </div>
                </div>

                <div className="space-y-8">
                    <section>
                        <h3 className="text-lg font-bold border-b border-gray-300 mb-4 pb-1">1. Executive Summary</h3>
                        <p className="text-gray-700 leading-relaxed">
                            {selectedSample?.notes || "No summary notes provided. Testing has been completed as per the requested plan."}
                        </p>
                    </section>
                    <section>
                        <h3 className="text-lg font-bold border-b border-gray-300 mb-4 pb-1">2. Test Results</h3>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-2 border">Test Performed</th>
                                    <th className="p-2 border">Findings</th>
                                    <th className="p-2 border">Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedSample?.testResults?.length ? (
                                    selectedSample.testResults.map((tr, i) => (
                                        <tr key={i} className="border-b">
                                            <td className="p-2 border font-medium">{tr.testType}</td>
                                            <td className="p-2 border">{tr.findings}</td>
                                            <td className="p-2 border">
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${tr.result === 'pass' ? 'bg-green-100 text-green-800' :
                                                    tr.result === 'fail' ? 'bg-red-100 text-red-800' : 'bg-gray-100'
                                                    }`}>
                                                    {tr.result.toUpperCase()}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={3} className="p-4 text-center text-gray-400">No test results recorded</td></tr>
                                )}
                            </tbody>
                        </table>
                    </section>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gray-50 dark:bg-slate-900 min-h-[600px] rounded-2xl p-6">
            {step === 1 && (
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Report Generation Wizard</h2>
                        <p className="text-gray-600 mt-2">Select a template to begin generating a standardized standard report</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {templates.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => { setTemplate(t.id); setStep(2); }}
                                className="flex items-start gap-4 p-6 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all text-left group"
                            >
                                <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 transition-colors">
                                    {t.icon}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">{t.title}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-2">{t.description}</p>
                                    <span className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs rounded font-medium">
                                        {t.compliance}
                                    </span>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-300 ml-auto self-center group-hover:text-blue-500" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="max-w-3xl mx-auto">
                    <button onClick={() => setStep(1)} className="mb-6 flex items-center text-gray-600 hover:text-gray-900">
                        ← Back to Templates
                    </button>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Select Data Source</h2>
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search samples, incidents..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                            {getAvailableData().map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => setSelectedDataId(item.id)}
                                    className={`w-full flex items-center p-4 border-b border-gray-100 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors text-left ${selectedDataId === item.id ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-inset ring-blue-500' : ''}`}
                                >
                                    <div className="flex-1">
                                        <div className="font-bold text-gray-900 dark:text-white">{item.title}</div>
                                        <div className="text-sm text-gray-500">{item.subtitle}</div>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {typeof item.date === 'string' ? new Date(item.date).toLocaleDateString() : 'N/A'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            disabled={!selectedDataId}
                            onClick={() => setStep(3)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            Generate Draft <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="flex gap-6 h-[800px]">
                    <div className="flex-1 overflow-y-auto bg-gray-200 dark:bg-slate-900 p-4 rounded-xl">
                        {renderReportPreview()}
                    </div>
                    <div className="w-64 space-y-4 shrink-0">
                        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700">
                            <h3 className="font-bold text-gray-900 dark:text-white mb-4">Actions</h3>
                            <div className="space-y-2">
                                <button className="w-full flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <Printer className="w-4 h-4" /> Print / PDF
                                </button>
                                <button className="w-full flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                                    <Share2 className="w-4 h-4" /> Share Link
                                </button>
                                <button className="w-full flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                                    <ExternalLink className="w-4 h-4" /> Customer Portal
                                </button>
                            </div>
                        </div>
                        <button onClick={() => { setStep(1); setSelectedDataId(null); }} className="w-full text-gray-500 hover:text-gray-900 text-sm">
                            Start Over
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
